import React, { useState, useRef, Fragment, useMemo } from 'react';
import SuccessBlock from './SuccessBlock';
import { handleFormEvents, pushEventToGTMDataLayer } from 'src/scripts/utils/gtm-data-layer';
import FormInput from '../../_shared/ui-components/YogaUI/FormInput';
import ButtonWithProgress from 'src/scripts/templates/_shared/ui-components/YogaUI/ButtonWithProgress';

const GladlyCsContactForm = () => {
  const [reason, setReason] = useState<string | null>(null);
  const [regardsTo, setRegardsTo] = useState<string>('');
  const [orderNo, setOrderNo] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [didYouKnowDisplay, setDidYouKnowDisplay] = useState<boolean>(false);
  const [didYouKnowText, setDidYouKnowText] = useState<JSX.Element | null>(null);
  const [isSuccessPage, setIsSuccessPage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const inRegardsToSelect = useRef<HTMLSelectElement>(null);

  const updateSubject = (type: string, subType: string, orderNoVal: string) => {
    let subjectValue = `${type} / ${subType}`;
    if (orderNoVal) subjectValue += ` (#${orderNoVal})`;
    setDidYouKnowSituation(subjectValue);
  };

  const predefinedMessageTemplates: {
    [key: string]: JSX.Element;
  } = useMemo(() => {
    const { email: customerEmail, tags } = window.getCustomerInfo?.() || {};
    const lowercasedTags = tags?.map((tag) => tag.toLowerCase());
    const loggedIn = Boolean(customerEmail);
    const proMessage = !loggedIn
      ? 'You are NOT currently logged in. '
      : lowercasedTags?.includes('pro-program') || lowercasedTags?.includes('pro program')
      ? 'You are currently logged into a Pro Membership on this device.'
      : `You are logged into the account ${customerEmail}, which does not have an active Pro membership.`;

    return {
      'Pro Program / Problem Using Discount': (
        <Fragment>
          {proMessage} Your Pro Discount can only be applied when you have logged in, by going to{' '}
          <a href="/account">My Account</a>, <strong>before</strong> going to Checkout. If your Pro membership is
          active, you will see a message at the top of the <a href="/account">My Account</a> page with the Pro discount
          code.
        </Fragment>
      ),
      'Order Status/Changes / Order Status': (
        <Fragment>
          Orders typically take 1-3 business days to ship. You will receive an email confirmation and tracking number
          once your order has shipped. If you have an aloyoga.com account, you can log in and view order status and
          tracking on the My Dashboard page, under recent orders. Or, check your order status by simply clicking{' '}
          <a href="/pages/track-my-order">here</a>. You can opt into text order updates on the confirmation page.
        </Fragment>
      ),
      'Returns / Gift Returns': (
        <Fragment>
          Gifted items purchased online may be returned for store credit. The item must be unworn, unwashed, and unused
          with the original tags attached. If you are the gift recipient reaching out, please ensure you enter your
          email address in the above email field.
        </Fragment>
      ),
    };
  }, []);

  const setDidYouKnowSituation = (subject: string) => {
    const message = predefinedMessageTemplates[subject];
    if (message) {
      setDidYouKnowText(message);
      setDidYouKnowDisplay(true);
    } else {
      setDidYouKnowDisplay(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    const formData = {
      contact: {
        first_name: '',
        last_name: '',
        full_name: name,
        email,
      },
      details: {
        message,
        order_no: orderNo,
        help_with: reason,
        regards: regardsTo,
      },
      locale: {
        language: window.Shopify?.locale ?? 'en',
        country: window.Shopify?.country ?? 'US',
        currency: window.Shopify?.currency.active ?? 'USD', //optional
      },
    };

    const gladlyHost = process.env.REACT_APP_GLADLY_HOST;

    if (!gladlyHost) {
      console.error('Missing Gladly configuration: Host');
      return null;
    }

    try {
      const response = await fetch(`${gladlyHost}/v2/web-form`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Form submission failed: ${response.status} - ${response.statusText}`);
      }

      setIsSuccessPage(true);
      pushEventToGTMDataLayer({ event: 'contact_form_submit', form: 'contact' });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const contactFormStartEvent = () => handleFormEvents('contact_form_start');

  if (isSuccessPage) return <SuccessBlock />;

  return (
    <div className="formWrapper">
      <div className="form-header">
        <h1 style={{ marginBottom: 6 }} className="h1 uppercase center">
          Email us
        </h1>
        <p style={{ marginBottom: 30 }} className="large-p center">
          We’re here to help! Please fill out the information below.
        </p>
        <p style={{ marginBottom: 26 }} className="pre-headline uppercase">
          <span style={{ color: '#FF0707' }}>*</span>required fields
        </p>
      </div>
      <form className="cs-form" onSubmit={handleSubmit}>
        <div className="mb-lg">
          <label className="pre-headline uppercase" htmlFor="name">
            <span style={{ color: '#FF0707' }}>*</span>Name
          </label>
          <FormInput
            options={{
              id: 'name',
              name: 'name',
              label: '',
              type: 'text',
              required: true,
              autocomplete: 'name',
              wrapperClasses: 'w-100',
              maxLength: 80,
              labelClasses: 'pre-headline uppercase',
              inputClasses: 'input-field',
              errorText: 'Name is required',
              defaultValue: name,
              onChange: (e) => {
                const value = (e.target as HTMLInputElement).value;
                setName(value);
                contactFormStartEvent();
              },
            }}
          />
        </div>
        <div className="mb-md">
          <label className="pre-headline uppercase" htmlFor="email">
            <span style={{ color: '#FF0707' }}>*</span>Email (Please use address used to place order if relevant)
          </label>
          <FormInput
            options={{
              id: 'email',
              name: 'email',
              label: '',
              type: 'email',
              required: true,
              autocomplete: 'email',
              wrapperClasses: 'w-100',
              maxLength: 80,
              labelClasses: 'pre-headline uppercase',
              inputClasses: 'input-field',
              errorText: 'Please enter a valid email address',
              defaultValue: email,
              onChange: (e) => {
                const value = (e.target as HTMLInputElement).value;
                setEmail(value);
                contactFormStartEvent();
              },
            }}
          />
        </div>

        <div className="form-fields-row">
          <div className="form-field">
            <label htmlFor="contact-reason" className="pre-headline uppercase">
              <span style={{ color: '#FF0707' }}>*</span> What can we help with?
            </label>
            <select
              id="contact-reason"
              name="contactReason"
              className="small-p form-control"
              required
              value={reason || ''}
              onChange={(e) => {
                const selected = e.target.value;
                setReason(selected);
                setRegardsTo('');
                updateSubject(reason || '', selected, orderNo);
                contactFormStartEvent();
              }}
            >
              <option value="" hidden>
                Select one
              </option>
              {Object.keys(OPTIONS_MAP).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label
              htmlFor="regards-to-select"
              className="pre-headline uppercase"
              style={reason ? undefined : { opacity: 0.5 }}
            >
              <span style={{ color: '#FF0707' }}>*</span> In regards to?
            </label>
            <select
              ref={inRegardsToSelect}
              id="regards-to-select"
              name="regardsTo"
              className="small-p form-control"
              required
              disabled={!reason}
              value={regardsTo}
              onChange={(e) => {
                const selected = e.target.value;
                setRegardsTo(selected);
                updateSubject(reason || '', selected, orderNo);
                contactFormStartEvent();
              }}
            >
              <InRegardsTo reason={reason} />
            </select>
          </div>
        </div>

        {didYouKnowDisplay && (
          <div id="DidYouKnowSection" className="mb-md mr-md">
            <h4 className="h4">ℹ️ Did you know?</h4>
            <p className="small-p">{didYouKnowText}</p>
          </div>
        )}

        <div className="mb-md">
          <label htmlFor="order-number-input" className="pre-headline uppercase">
            Order # (Example:1160410):{' '}
          </label>
          <FormInput
            options={{
              id: 'order-number-input',
              name: 'orderNumber',
              label: '',
              type: 'text',
              maxLength: 8,
              labelClasses: 'pre-headline uppercase',
              wrapperClasses: 'field-width',
              inputClasses: 'input-field',
              defaultValue: orderNo,
              onChange: (e) => {
                const value = (e.target as HTMLInputElement).value;
                setOrderNo(value);
                updateSubject(reason || '', regardsTo, value);
                contactFormStartEvent();
              },
            }}
          />
        </div>

        {/* Message */}
        <div className="mb-md">
          <label className="pre-headline uppercase" htmlFor="description">
            <span style={{ color: '#FF0707' }}>*</span>Message
          </label>
          <div className="yoga-ui-text-input full-width">
            <label htmlFor="description" className="pre-headline uppercase">
              <span style={{ color: '#FF0707' }}>*</span> Message
            </label>
            <textarea
              id="description"
              name="description"
              className="w-100 small-p"
              style={{ height: 150 }}
              required
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                contactFormStartEvent();
              }}
            />
          </div>
        </div>
        <div style={{ maxWidth: 170, marginTop: 45, fontWeight: 900, marginLeft: 'auto', marginRight: 'auto' }}>
          <ButtonWithProgress
            options={{
              buttonClasses: 'main-button',
              showProgressAnimation: isLoading,
              cta: 'Submit',
              type: 'submit',
              disabled: isLoading,
            }}
          />
        </div>
      </form>
    </div>
  );
};

const OPTIONS_MAP: { [key: string]: string[] } = {
  'Alo Moves': ['30 Day free trial', 'Annual Subscription', 'Other'],
  'Order Status/Changes': ['Order Status', 'Make a Change', 'Gift Card', 'Cancel', 'Other'],
  Returns: ['Make a Return', 'Return Status', 'Apparel Defect', 'Gift Returns', 'Other'],
  'Pro Program': ['Application', 'Problem Using Discount', 'Other'],
  'Friend Referral Program': ['Code Not Working', 'Other'],
  'Product Inquiries': ['Fit', 'Styling', 'Back in Stock', 'Other'],
  'Stores/Studios': ['Product Availability', 'Information', 'Other'],
  Wholesale: ['Other'],
  Other: ['Other'],
};

const InRegardsTo: React.FC<{ reason: string | null }> = ({ reason }) => {
  const options = OPTIONS_MAP[reason || ''] || [];
  return (
    <>
      <option value="" hidden>
        Select one
      </option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </>
  );
};

export default GladlyCsContactForm;
